import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import commentThreadService from './commentThreadService';

import type {
    CommentThreadReplyRequest,
    ReviewAnswerCommentThreads,
} from '@/types/review/CommentThread';

import { DEFAULT_QUERY_OPTIONS } from '@/api/api';
import { isCommentRepliesDisabledRefusal } from '@/api/review-services/comment-reply/commentRepliesDisabledRefusal';
import { CommentThreadMutationKey, CommentThreadQueryKey } from '@/constants/QueryKeys';

export const useGetCommentThreads = (threadKey: string) =>
    useQuery({
        ...DEFAULT_QUERY_OPTIONS,
        queryKey: [CommentThreadQueryKey.COMMENT_THREAD, threadKey],
        queryFn: () => commentThreadService.getCommentThreads(threadKey),
        enabled: Boolean(threadKey),
    });

export const useReplyInCommentThread = (threadKey: string) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationKey: [CommentThreadMutationKey.REPLY_IN_COMMENT_THREAD, threadKey],
        mutationFn: (request: CommentThreadReplyRequest) =>
            commentThreadService.replyInCommentThread({ threadKey, request }),
        // Replies were turned off since the thread was read: reading it again closes its reply fields.
        onError: (error) => {
            if (isCommentRepliesDisabledRefusal(error)) {
                void queryClient.invalidateQueries({
                    queryKey: [CommentThreadQueryKey.COMMENT_THREAD, threadKey],
                });
            }
        },
        // The server answers with the reply as it signs it, so the thread is completed without reading it again.
        onSuccess: (created, { reviewQuestionAnswerId }) =>
            queryClient.setQueryData<ReviewAnswerCommentThreads>(
                [CommentThreadQueryKey.COMMENT_THREAD, threadKey],
                (current) =>
                    current && {
                        ...current,
                        threads: current.threads.map((thread) =>
                            thread.reviewQuestionAnswerId === reviewQuestionAnswerId
                                ? { ...thread, replies: [...thread.replies, created] }
                                : thread,
                        ),
                    },
            ),
    });
};
